import React from "react";
import { Upload, Button, Radio, Alert, Empty } from "antd";
import { InboxOutlined, UploadOutlined, PictureOutlined } from "@ant-design/icons";
import ImageCard from "../ImageCard";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGES_PER_PRODUCT,
  MAX_IMAGE_SIZE_MB,
} from "../../constants";

const SectionHead = ({ icon, title, note }) => (
  <div className="mmh-section-head">
    <span className="mmh-icon-chip">{icon}</span>
    <div className="mmh-section-head-text">
      <div className="mmh-section-title">{title}</div>
      {note && <div className="mmh-section-note">{note}</div>}
    </div>
  </div>
);

const ImagesStep = ({
  imageItems,
  errors,
  masters,
  onAddFile,
  onUpdateImage,
  onRemoveImage,
  onSetDp,
  onMoveImage,
  onCopyFromPrevious,
}) => {
  const dpUid = imageItems.find((item) => item.isDp)?.uid;
  const atLimit = imageItems.length >= MAX_IMAGES_PER_PRODUCT;

  return (
    <section className="mmh-section">
      <SectionHead
        icon={<PictureOutlined />}
        title="Product Images"
        note="Tag the first photo and the rest arrive pre-filled — change only what differs. Nothing is uploaded until you save."
      />

      <Upload.Dragger
        multiple
        showUploadList={false}
        disabled={atLimit}
        accept={ACCEPTED_IMAGE_TYPES.join(",")}
        beforeUpload={(file) => {
          onAddFile(file);
          // Returning false keeps antd from performing its own upload — we
          // hold the files in state and send them with the single save.
          return false;
        }}
        className="mmh-uploader"
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {atLimit ? "Image limit reached" : "Drag & drop product images here"}
        </p>
        <p className="ant-upload-hint">
          {atLimit
            ? `You can attach up to ${MAX_IMAGES_PER_PRODUCT} images per product.`
            : `JPG, PNG, WEBP or PDF — up to ${MAX_IMAGE_SIZE_MB}MB each, ${MAX_IMAGES_PER_PRODUCT} images max`}
        </p>
        {!atLimit && (
          <Button icon={<UploadOutlined />} className="mmh-upload-btn">
            Browse Files
          </Button>
        )}
      </Upload.Dragger>

      {errors.form?.images && (
        <Alert type="error" showIcon className="mmh-step-alert" message={errors.form.images} />
      )}

      {imageItems.length > 0 ? (
        <>
          <div className="mmh-image-count">
            {imageItems.length} image{imageItems.length > 1 ? "s" : ""} attached
          </div>

          <Radio.Group
            className="mmh-image-list"
            value={dpUid}
            onChange={(e) => onSetDp(e.target.value)}
          >
            {imageItems.map((item, index) => (
              <ImageCard
                key={item.uid}
                item={item}
                index={index}
                total={imageItems.length}
                masters={masters}
                errors={errors.images?.[item.uid] || {}}
                onChange={onUpdateImage}
                onRemove={onRemoveImage}
                onMove={onMoveImage}
                onCopyFromPrevious={onCopyFromPrevious}
              />
            ))}
          </Radio.Group>
        </>
      ) : (
        <Empty
          className="mmh-image-empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No images attached yet"
        />
      )}
    </section>
  );
};

export default ImagesStep;
